import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Alert, Button, Checkbox, Modal, Space, Spin, Steps, Typography, message,
} from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Printer from '../../printer/Printer.component';
import ReportingPrintCapture from './ReportingPrintCapture';
import useCategoryLabelMaps from './useCategoryLabelMaps';
import {
    buildReportingPrintPayload,
    loadSelectedDashboardsPrintData,
    resolveReportingFilterLabels,
} from './reportingPrintUtils';
import ApiErrorAlert from '../../common/ApiErrorAlert';

const { Text, Paragraph } = Typography;

function dashboardId(db) {
    return db?.id ?? db?.Id;
}

export default function ReportingDashboardPrintModal({
    open,
    onClose,
    dashboards = [],
    dateRange,
    globalFilters = {},
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const store = useStore();
    const userLogin = useSelector(state => state.userLogin);
    const functions = useSelector(state => state.reportFunctionList?.items ?? []);
    const categoryLabelMaps = useCategoryLabelMaps();
    const { userInfo } = userLogin;

    const [step, setStep] = useState(0);
    const [selectedIds, setSelectedIds] = useState([]);
    const [preparing, setPreparing] = useState(false);
    const [prepareError, setPrepareError] = useState(null);
    const [printData, setPrintData] = useState(null);
    const [captureItems, setCaptureItems] = useState([]);
    const [pendingResults, setPendingResults] = useState(null);
    const captureRef = useRef(null);

    const sortedDashboards = useMemo(
        () => [...dashboards].sort(
            (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
                || String(a.name ?? '').localeCompare(String(b.name ?? '')),
        ),
        [dashboards],
    );

    useEffect(() => {
        if (!open) return;
        setStep(0);
        setPrepareError(null);
        setPrintData(null);
        setCaptureItems([]);
        setPendingResults(null);
        setSelectedIds(sortedDashboards.map(dashboardId).filter(Boolean));
    }, [open, sortedDashboards]);

    const selectedDashboards = useMemo(
        () => sortedDashboards.filter(db => selectedIds.includes(dashboardId(db))),
        [sortedDashboards, selectedIds],
    );

    const toggleDashboard = (id, checked) => {
        setSelectedIds(prev => (
            checked ? [...new Set([...prev, id])] : prev.filter(x => x !== id)
        ));
    };

    const toggleAll = (checked) => {
        setSelectedIds(checked ? sortedDashboards.map(dashboardId).filter(Boolean) : []);
    };

    const buildPrintPayload = async (dashboardResults, chartImagesByDashboard) => {
        const filterLabels = await resolveReportingFilterLabels(
            globalFilters,
            dispatch,
            () => store.getState(),
        );
        return buildReportingPrintPayload({
            dashboardResults,
            chartImagesByDashboard,
            userInfo,
            dateRange,
            globalFilters,
            filterLabels,
        });
    };

    useEffect(() => {
        if (!pendingResults || captureItems.length === 0) return undefined;

        let cancelled = false;

        const runCapture = async () => {
            await new Promise(resolve => setTimeout(resolve, 500));
            if (cancelled) return;

            try {
                const images = await captureRef.current?.captureAll?.() ?? {};
                const chartImagesByDashboard = {};

                for (const result of pendingResults) {
                    const dashId = dashboardId(result.dashboard);
                    chartImagesByDashboard[dashId] = {};
                    for (const item of result.captureItems) {
                        if (images[item.id]) {
                            chartImagesByDashboard[dashId][item.id] = images[item.id];
                        }
                    }
                }

                const payload = await buildPrintPayload(pendingResults, chartImagesByDashboard);

                if (!cancelled) {
                    setPrintData(payload);
                    setStep(1);
                }
            } catch (error) {
                if (!cancelled) {
                    setPrepareError(error.message || t('Failed to capture charts'));
                }
            } finally {
                if (!cancelled) {
                    setPreparing(false);
                    setCaptureItems([]);
                    setPendingResults(null);
                }
            }
        };

        runCapture();

        return () => {
            cancelled = true;
        };
    }, [pendingResults, captureItems, userInfo, dateRange, globalFilters, dispatch, store, t]);

    const handlePrepare = async () => {
        if (selectedDashboards.length === 0) {
            message.warning(t('Select at least one dashboard'));
            return;
        }

        setPreparing(true);
        setPrepareError(null);
        setPrintData(null);

        try {
            const dashboardResults = await loadSelectedDashboardsPrintData({
                dashboards: selectedDashboards,
                dateRange,
                globalFilters,
                dispatch,
                labelMapsLookup: categoryLabelMaps,
                functions,
            });

            const flatCaptureItems = dashboardResults.flatMap(r => r.captureItems);

            if (flatCaptureItems.length === 0) {
                const payload = await buildPrintPayload(dashboardResults, {});
                setPrintData(payload);
                setStep(1);
                setPreparing(false);
                return;
            }

            setPendingResults(dashboardResults);
            setCaptureItems(flatCaptureItems);
        } catch (error) {
            console.error('Reporting print prepare failed:', error);
            setPrepareError(error.message || t('Failed to prepare report'));
            setPreparing(false);
        }
    };

    const handleClose = () => {
        if (preparing) return;
        setStep(0);
        setPrintData(null);
        setCaptureItems([]);
        setPendingResults(null);
        onClose?.();
    };

    if (!userInfo?.token) return null;

    return (
        <>
            <ReportingPrintCapture ref={captureRef} captureItems={captureItems} />

            <Modal
                title={(
                    <Space>
                        <PrinterOutlined />
                        {t('SQL Dashboard Report')}
                    </Space>
                )}
                open={open}
                onCancel={handleClose}
                footer={step === 0 ? (
                    <Space>
                        <Button onClick={handleClose} disabled={preparing}>{t('Cancel')}</Button>
                        <Button
                            type="primary"
                            loading={preparing}
                            disabled={selectedDashboards.length === 0}
                            onClick={handlePrepare}
                        >
                            {t('Continue')}
                        </Button>
                    </Space>
                ) : (
                    <Button onClick={handleClose}>{t('Close')}</Button>
                )}
                width="95%"
                style={{ top: 20 }}
                destroyOnClose
                maskClosable={!preparing}
                closable={!preparing}
            >
                <Steps
                    current={step}
                    size="small"
                    style={{ marginBottom: 20 }}
                    items={[
                        { title: t('Select dashboards') },
                        { title: t('Report') },
                    ]}
                />

                {step === 0 && (
                    <div>
                        <Paragraph type="secondary">
                            {t('Choose which dashboards to include. Charts are rendered off-screen so they appear correctly in the PDF. Data tables are included below each chart.')}
                        </Paragraph>

                        {sortedDashboards.length === 0 ? (
                            <Alert
                                type="info"
                                showIcon
                                message={t('No dashboards available')}
                                description={t('Create or load default dashboards first.')}
                            />
                        ) : (
                            <>
                                <div style={{ marginBottom: 12 }}>
                                    <Checkbox
                                        indeterminate={
                                            selectedIds.length > 0
                                            && selectedIds.length < sortedDashboards.length
                                        }
                                        checked={selectedIds.length === sortedDashboards.length}
                                        onChange={e => toggleAll(e.target.checked)}
                                    >
                                        {t('Select all ({{count}})', { count: sortedDashboards.length })}
                                    </Checkbox>
                                </div>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {sortedDashboards.map((db) => {
                                        const id = dashboardId(db);
                                        const chartCount = (db.layoutJson?.charts ?? []).filter(
                                            c => !c.kind || c.kind === 'chart',
                                        ).length;
                                        return (
                                            <Checkbox
                                                key={id}
                                                checked={selectedIds.includes(id)}
                                                onChange={e => toggleDashboard(id, e.target.checked)}
                                            >
                                                <Space>
                                                    <span>{db.name}</span>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                        {chartCount} {chartCount === 1 ? t('chart') : t('charts')}
                                                    </Text>
                                                </Space>
                                            </Checkbox>
                                        );
                                    })}
                                </Space>
                            </>
                        )}

                        {preparing && (
                            <div style={{ textAlign: 'center', padding: '32px 0' }}>
                                <Spin size="large" />
                                <Paragraph type="secondary" style={{ marginTop: 12 }}>
                                    {t('Loading data and rendering charts for PDF…')}
                                </Paragraph>
                            </div>
                        )}

                        <ApiErrorAlert error={prepareError} style={{ marginTop: 16 }} />
                    </div>
                )}

                {step === 1 && printData && (
                    <Printer
                        configEndpoint="/print/configs/sql_reporting_dashboard"
                        generateEndpoint="/print/documents/generate"
                        data={printData}
                        autoGenerate
                        embed
                    />
                )}
            </Modal>
        </>
    );
}
